
import { defineComponent, reactive, onMounted, toRefs, h, ref, nextTick } from 'vue'
import { DoubleLeftOutlined, DoubleRightOutlined } from '@ant-design/icons-vue'
import f10ApiHk from '@/api/f10ApiHk'
import Table from '@/components/table.vue'
import Nodata from '@/components/page/nodata.vue'
import { useRoute } from 'vue-router'

export default defineComponent({
  props: {
    itemdata: {
      type: Object,
      default: () => ({})
    },
    token: {
      type: String,
      default: ''
    }
  },
  components: { Table, Nodata, DoubleLeftOutlined, DoubleRightOutlined },

  setup(props) {
    const route = useRoute()
    const radioGroupRef = ref()
    const leftIconShow = ref(false)
    const rightIconShow = ref(false)

    const pageData = reactive({
      data: [] as any[],
      radiolist: [] as string[], // 时间单选项
      radiovalue: '',            // 当前选择的时间
      columns: [
        { title: '股东名称', dataIndex: 'reporterName', key: 'reporterName', align: 'left' },
        {
          title: '最终控制人', dataIndex: 'orgIdentifier', key: 'orgIdentifier', align: 'left',
          customRender: ({ record }: any) => {
            return record.orgIdentifier === 1 ? '机构' : '个人';
          }
        },
        {
          title: '占总股本比',
          dataIndex: 'totalHeldRatioValue',
          key: 'totalHeldRatioValue',
          align: 'right',
          customRender: ({ record }: any) => {
            return record.formattedRatio || (record.totalHeldRatioValue + '%')
          }
        },
        {
          title: '持股数',
          dataIndex: 'shareHeldNum',
          key: 'shareHeldNum',
          align: 'right',
          customRender: ({ record }: any) => {
            return record.formattedShares || record.shareHeldNum
          }
        },
        {
          title: '持股变动',
          dataIndex: 'heldTotalChange',
          key: 'heldTotalChange',
          align: 'right',
          customRender: ({ record }: any) => {
            return h(
              'span',
              { style: { color: record.changeColor || 'inherit' } },
              record.formattedChange || record.heldTotalChange || '0'
            )
          }
        }
      ],
      spinning: true
    })
    const groupedData: Record<string, any[]> = {}

    onMounted(() => {
      const params = {
        data: {
          // market: 'NASDAQ', 
          // symbol: 'AAPL'
          market: route.query.market || 'US',
          symbol: route.query.code || 'PDD',
        },
        headers: {
          token: props.token || '3d5eff0d3c7ac7f3af30c337dcdf6944'
        }
      }

      f10ApiHk.getBriefshareholderListMg(params).then((res: any) => {
        console.log('API返回的完整数据:', res)
        if (res.data && res.data.list) {
          const list = res.data.list
          console.log('打印监听数据list', list)

          // 清空之前的数据
          Object.keys(groupedData).forEach(key => delete groupedData[key])

          // onMounted 数据处理部分
          list.forEach((item: any) => {
            const date = new Date(item.reportDate)
            const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1)
              .toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`

            if (!groupedData[formattedDate]) groupedData[formattedDate] = []

            // 占股比格式化 - 添加空值检查
            let formattedRatio = '--'
            if (item.totalHeldRatioValue !== null && item.totalHeldRatioValue !== undefined) {
              const ratio = parseFloat(item.totalHeldRatioValue)
              if (!isNaN(ratio)) {
                if (ratio === 0) {
                  formattedRatio = '<0.01%'
                } else {
                  formattedRatio = ratio.toFixed(2) + '%'
                }
              }
            }

            // 持股变动格式化 - 转换为万单位
            let formattedChange = '--'
            let changeColor = ''
            if (item.heldTotalChange !== null && item.heldTotalChange !== undefined) {
              const changeValue = item.heldTotalChange / 10000 // 转换为万
              if (item.heldTotalChange > 0) {
                formattedChange = '+' + changeValue.toFixed(2) + '万'
                changeColor = 'red'
              } else if (item.heldTotalChange < 0) {
                formattedChange = changeValue.toFixed(2) + '万'
                changeColor = 'green'
              } else if (item.heldTotalChange === 0) {
                formattedChange = '0万'
                changeColor = ''
              }
            }

            // 持股数格式化 - 添加空值检查
            let formattedShares = '--'
            if (item.shareHeldNum !== null && item.shareHeldNum !== undefined) {
              const shares = parseFloat(item.shareHeldNum)
              if (!isNaN(shares)) {
                formattedShares = (shares / 100000000).toFixed(2) + '亿'
              }
            }

            groupedData[formattedDate].push({
              ...item,
              formattedShares,
              formattedRatio,
              formattedChange,
              changeColor
            })
          })

          // 设置时间单选项，默认选最新日期
          pageData.radiolist = Object.keys(groupedData).sort((a, b) => (a < b ? 1 : -1))
          pageData.radiovalue = pageData.radiolist[0]
          pageData.data = groupedData[pageData.radiovalue] || []

          // 检查滚动状态
          nextTick(() => {
            checkScrollStatus()
            // 添加滚动事件监听器
            if (radioGroupRef.value) {
              const container = radioGroupRef.value.$el
              container.addEventListener('scroll', checkScrollStatus)
            }
          })
        }
        pageData.spinning = false
      }).catch((error) => {
        console.log('API调用失败:', error)
        pageData.spinning = false
      })
    })
    // 切换事件：直接用缓存的 groupedData
    const changeradio = (e: any) => {
      const selectedDate = e.target.value
      pageData.data = groupedData[selectedDate] || []
    }

    // 滚动到目标位置
    const scrollToTarget = (direction: 'left' | 'right') => {
      if (!radioGroupRef.value) return

      const container = radioGroupRef.value.$el
      const scrollAmount = 200 // 每次滚动的距离
      const { scrollLeft, scrollWidth, clientWidth } = container

      // 使用 requestAnimationFrame 确保平滑滚动
      requestAnimationFrame(() => {
        if (direction === 'left') {
          // 向左滚动，确保不会滚动到负数
          container.scrollLeft = Math.max(0, scrollLeft - scrollAmount)
        } else {
          // 向右滚动，确保不会超出最大滚动距离
          const maxScrollLeft = scrollWidth - clientWidth
          container.scrollLeft = Math.min(maxScrollLeft, scrollLeft + scrollAmount)
        }

        // 滚动后检查图标显示状态
        nextTick(() => {
          checkScrollStatus()
        })
      })
    }

    // 检查滚动状态，控制左右图标的显示
    const checkScrollStatus = () => {
      if (!radioGroupRef.value) return

      const container = radioGroupRef.value.$el
      const { scrollLeft, scrollWidth, clientWidth } = container

      leftIconShow.value = scrollLeft > 0
      // 当滚动到最右边时（允许1px的误差），隐藏右图标
      rightIconShow.value = scrollLeft < scrollWidth - clientWidth - 1
    }
    // 单选切换事件
    // const changeradio = (e: any) => {
    //   const selectedDate = e.target.value
    //   // 根据 radiovalue 过滤出对应数据
    //   f10ApiHk.getBriefshareholderList({
    //     data: { market: 'HK', symbol: '00700' },
    //     headers: { token: props.token || '3d5eff0d3c7ac7f3af30c337dcdf6944' }
    //   }).then((res: any) => {
    //     if (res.data && res.data.list) {
    //       const list = res.data.list.filter((item: any) => {
    //         const date = new Date(item.reportDate)
    //         const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1)
    //           .toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
    //         return formattedDate === selectedDate
    //       }).map((item: any) => ({
    //         ...item,
    //         formattedShares: (item.heldTotalNumLongPosition / 100000000).toFixed(2) + '亿股'
    //       }))
    //       pageData.data = list
    //     }
    //   })
    // }

    return {
      ...toRefs(pageData),
      radioGroupRef,
      leftIconShow,
      rightIconShow,
      changeradio,
      scrollToTarget
    }
  }
})
